import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { useFacility } from '../context/FacilityContext';
import apiClient from '../utils/apiClient';

// Setup -> Late Fees. The automation config the billing scheduler reads:
// an ordered escalation schedule (each tier applies a flat amount or a
// catalog fee), the NSF fee, and the dunning reminder threshold. Replaces
// the legacy flat facility.late_fee_* fields.
//
// NOTHING HERE STARTS A LIEN. No code path sets a rental to Lien — that
// belongs to the lien workflow, where the statutory notices and deadlines
// are tracked. This screen used to say "late & lien processing" and offered
// a "Move to Lien" tier action that moved nothing, which implied a billing
// sweep could begin a legal process on its own.
//
// The grace period is no longer edited here either: a lease states one
// first-late day, and that day IS the first tier. Kept as a separate field
// it could be set ABOVE tier 1, which silently skipped that tier — no fee,
// no warning. It is now derived (services/billing.resolve_grace_days).
const Tier = ({ tier, fees, onChange, onRemove }) => {
  const inputCls =
    'px-3 py-2 rounded-lg border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500';
  return (
    <div className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-200/80 bg-slate-50/50 p-3">
      <div>
        <label className="block text-[12px] text-slate-500 mb-1">
          Days past due
        </label>
        <input
          type="number"
          className={`${inputCls} w-28`}
          value={tier.days_past_due}
          onChange={(e) =>
            onChange({ ...tier, days_past_due: parseInt(e.target.value, 10) || 0 })
          }
        />
        <p className="text-[10px] text-slate-400 mt-0.5 w-28">
          {tier.days_past_due > 0
            ? `= the ${tier.days_past_due + 1}${['th','st','nd','rd'][((tier.days_past_due + 1) % 10 < 4 && Math.floor((tier.days_past_due + 1) % 100 / 10) !== 1) ? (tier.days_past_due + 1) % 10 : 0]} for rent due the 1st`
            : 'days after the due date'}
        </p>
      </div>
      <div>
        <label className="block text-[12px] text-slate-500 mb-1">
          Catalog fee
        </label>
        <select
          className={`${inputCls} w-44`}
          value={tier.fee_id || ''}
          onChange={(e) =>
            onChange({ ...tier, fee_id: e.target.value || null })
          }
        >
          <option value="">— flat amount —</option>
          {fees.map((f) => (
            <option key={f._id} value={f._id}>
              {f.name} (${Number(f.amount).toFixed(2)})
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-[12px] text-slate-500 mb-1">
          Flat amount ($)
        </label>
        <input
          type="number"
          step="0.01"
          disabled={!!tier.fee_id}
          className={`${inputCls} w-28 disabled:opacity-40`}
          value={tier.flat_amount ?? ''}
          onChange={(e) =>
            onChange({
              ...tier,
              flat_amount:
                e.target.value === '' ? null : parseFloat(e.target.value),
            })
          }
        />
      </div>
      <div>
        <label className="block text-[12px] text-slate-500 mb-1">Action</label>
        <select
          className={`${inputCls} w-36`}
          value={tier.action}
          onChange={(e) => onChange({ ...tier, action: e.target.value })}
        >
          <option value="late">Mark Delinquent</option>
          <option value="none">Fee only</option>
        </select>
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="p-2 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
        title="Remove tier"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
};

const LateLien = () => {
  const { facilityId } = useFacility();
  const [cfg, setCfg] = useState(null);
  const [fees, setFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    try {
      const [c, f] = await Promise.all([
        apiClient.get(`/api/facilities/${facilityId}/fees/late-lien`),
        apiClient.get(`/api/facilities/${facilityId}/fees/`),
      ]);
      setCfg(c.data);
      setFees(f.data);
    } catch (e) {
      setError('Could not load settings');
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setError('');
    setSaved(false);
    try {
      await apiClient.put(`/api/facilities/${facilityId}/fees/late-lien`, {
        enabled: cfg.enabled,
        grace_period_days: cfg.grace_period_days,
        tiers: cfg.tiers,
        nsf_fee_id: cfg.nsf_fee_id || null,
        reminder_after_days: cfg.reminder_after_days,
      });
      setSaved(true);
    } catch (e2) {
      setError(e2.response?.data?.detail || 'Save failed');
    }
  };

  if (loading || !cfg) {
    return <div className="text-[13px] text-slate-400">Loading…</div>;
  }

  const numCls =
    'w-28 px-3 py-2 rounded-lg border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500';

  return (
    // Rendered embedded inside FeesSettings (Late / Lien tab). The page H1 +
    // tab strip come from that wrapper, so this view drops its own page title
    // and keeps just the explanatory subtitle.
    <form onSubmit={save} className="max-w-3xl">
      <div className="mb-5">
        <p className="text-[13px] text-slate-400">
          The billing engine applies these automatically to past-due
          rentals each day.
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 border border-red-200/80 px-3 py-2.5 text-[13px] text-red-600">
          {error}
        </div>
      )}
      {saved && (
        <div className="mb-4 rounded-lg bg-emerald-50 border border-emerald-200/80 px-3 py-2.5 text-[13px] text-emerald-700">
          Settings saved.
        </div>
      )}

      <div className="rounded-xl border border-slate-200/80 bg-white p-5 space-y-5">
        <label className="flex items-center gap-2 text-[13px] font-medium text-slate-700">
          <input
            type="checkbox"
            checked={cfg.enabled}
            onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
          />
          Automatic late fees enabled
        </label>

        {/* Grace period is NOT edited here. A lease names one first-late day
            ("$25 if rent is not received by the 5th"), and that day IS the
            first tier below — so this was a second, independently editable
            field for the same fact. Because grace acts as a floor, setting it
            ABOVE the first tier silently skipped that tier: no fee, no error,
            no warning. It is now derived from the earliest tier
            (services/billing.resolve_grace_days), which makes the
            contradiction unrepresentable. The stored value is left untouched
            and is still honoured for facilities that have no tiers at all. */}
        <div className="flex flex-wrap gap-6">
          <div>
            <label className="block text-[13px] font-medium text-slate-700 mb-1.5">
              Send reminder after (days late)
            </label>
            <input
              type="number"
              className={numCls}
              value={cfg.reminder_after_days}
              onChange={(e) =>
                setCfg({
                  ...cfg,
                  reminder_after_days: parseInt(e.target.value, 10) || 0,
                })
              }
            />
            <p className="text-[10px] text-slate-400 mt-0.5">
              {cfg.reminder_after_days > 0
                ? `= the ${cfg.reminder_after_days + 1}${['th','st','nd','rd'][((cfg.reminder_after_days + 1) % 10 < 4 && Math.floor((cfg.reminder_after_days + 1) % 100 / 10) !== 1) ? (cfg.reminder_after_days + 1) % 10 : 0]} for rent due the 1st`
                : 'days after the due date'}
            </p>
          </div>
          <div>
            <label className="block text-[13px] font-medium text-slate-700 mb-1.5">
              NSF / declined-payment fee
            </label>
            <select
              className="w-52 px-3 py-2 rounded-lg border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              value={cfg.nsf_fee_id || ''}
              onChange={(e) =>
                setCfg({ ...cfg, nsf_fee_id: e.target.value || null })
              }
            >
              <option value="">— none —</option>
              {fees.map((f) => (
                <option key={f._id} value={f._id}>
                  {f.name} (${Number(f.amount).toFixed(2)})
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="text-[14px] font-semibold text-slate-900">
                Escalation schedule
              </h3>
              <p className="text-[11px] text-slate-400">
                Late-notice emails/texts fire the SAME day as each tier —
                saving here updates the notice timing automatically.
              </p>
              {/* Spells out what each tier actually triggers. Since
                  2026-07-29 the Delinquent flip follows the tier ACTION:
                  the first tier set to "Mark Delinquent" is the flip day;
                  "Fee only" tiers charge without changing status. */}
              <ul className="mt-1.5 text-[11px] text-slate-500 list-disc pl-4 space-y-0.5">
                <li>
                  Every tier charges its own fee on its own day.
                </li>
                <li>
                  The tenant is marked delinquent at the first tier whose
                  Action is <strong>Mark Delinquent</strong> — "Fee only"
                  tiers charge without changing status.
                </li>
                <li>
                  <strong>Last tier</strong> is when gate access is locked.
                </li>
              </ul>
            </div>
            <button
              type="button"
              onClick={() =>
                setCfg({
                  ...cfg,
                  tiers: [
                    ...cfg.tiers,
                    {
                      days_past_due: 5,
                      fee_id: null,
                      flat_amount: 15,
                      action: 'late',
                    },
                  ],
                })
              }
              className="inline-flex items-center gap-1.5 py-1.5 px-3 rounded-lg bg-blue-600 text-[13px] font-medium text-white hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-4 w-4" /> Add tier
            </button>
          </div>
          <div className="space-y-2">
            {cfg.tiers.length === 0 ? (
              <p className="text-[13px] text-slate-400">
                No tiers — no automatic fees will be applied.
              </p>
            ) : (
              cfg.tiers.map((t, i) => (
                <Tier
                  key={i}
                  tier={t}
                  fees={fees}
                  onChange={(nt) => {
                    const next = [...cfg.tiers];
                    next[i] = nt;
                    setCfg({ ...cfg, tiers: next });
                  }}
                  onRemove={() =>
                    setCfg({
                      ...cfg,
                      tiers: cfg.tiers.filter((_, j) => j !== i),
                    })
                  }
                />
              ))
            )}
          </div>
        </div>
      </div>

      <button
        type="submit"
        className="mt-5 py-2 px-4 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 transition-colors"
      >
        Save settings
      </button>
    </form>
  );
};

export default LateLien;
